/* Päeva kaar: light layer on top of server-rendered HTML.
   Everything here is progressive: without this file every section shows its
   own background, both ritual panels are visible and quick add posts the form. */
(() => {
  const root = document.documentElement;
  const sections = document.querySelectorAll('.arc[data-arc]');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const MOMENTS = {
    dawn: '#FBF8F2', 'dawn-2': '#F3EEE5', day: '#EEE4D3', golden: '#E5D5BC',
    dusk: '#3F4A30', night: '#1E2B25', 'night-2': '#26362E', clay: '#D9B48F'
  };

  if (sections.length) {
    root.classList.add('arc-live');
    root.style.setProperty('--arc-page', MOMENTS[sections[0].dataset.arc] || MOMENTS.dawn);

    // The page background follows whichever moment sits in the middle of the viewport.
    const bgObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          root.style.setProperty('--arc-page', MOMENTS[entry.target.dataset.arc] || MOMENTS.dawn);
        }
      });
    }, { rootMargin: '-48% 0px -48% 0px' });
    sections.forEach((s) => bgObserver.observe(s));
  }

  // Sections ease in once, the first time they are seen.
  const revealObserver = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  document.querySelectorAll('.arc-reveal').forEach((el) => revealObserver.observe(el));

  // Scroll-linked values: thread drawing and gentle parallax. One rAF per frame at most.
  const threads = [...document.querySelectorAll('.arc-thread')].map((svg) => ({ svg, host: svg.parentElement }));
  const parallax = [...document.querySelectorAll('[data-arc-par]')];
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    threads.forEach(({ svg, host }) => {
      const r = host.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 2) return;
      const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.35)));
      svg.style.setProperty('--arc-p', p.toFixed(3));
    });
    if (reduce.matches) return;
    parallax.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const factor = parseFloat(el.dataset.arcPar) || 0.06;
      const offset = (r.top + r.height / 2 - vh / 2) * factor;
      el.style.setProperty('--arc-par', offset.toFixed(1));
    });
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };
  if (threads.length || parallax.length) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // AM / PM ritual: one stage, the light changes with the choice.
  document.querySelectorAll('[data-arc-ritual]').forEach((ritual) => {
    const buttons = ritual.querySelectorAll('[data-moment]');
    const panels = ritual.querySelectorAll('[data-panel]');
    const set = (moment, focus) => {
      ritual.dataset.moment = moment;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.moment === moment)));
      panels.forEach((p) => {
        const active = p.dataset.panel === moment;
        p.toggleAttribute('inert', !active);
        p.setAttribute('aria-hidden', String(!active));
      });
      if (focus) ritual.querySelector(`[data-panel="${moment}"] h3`)?.focus({ preventScroll: true });
    };
    buttons.forEach((b) => b.addEventListener('click', () => set(b.dataset.moment, false)));
    set(ritual.dataset.moment || 'am', false);
  });

  // Mushroom page filter chips: every card is in the HTML; chips only hide.
  document.querySelectorAll('[data-arc-filter]').forEach((sec) => {
    const chips = sec.querySelector('.arc-m-chips');
    const items = sec.querySelectorAll('.arc-m-grid > li[data-kind]');
    if (!chips) return;
    const present = new Set([...items].map((li) => li.dataset.kind));
    chips.querySelectorAll('[data-kind]').forEach((b) => {
      if (b.dataset.kind !== 'all' && !present.has(b.dataset.kind)) b.remove();
    });
    chips.hidden = false;
    chips.addEventListener('click', (e) => {
      const b = e.target.closest('[data-kind]');
      if (!b) return;
      chips.querySelectorAll('[data-kind]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      items.forEach((li) => { li.hidden = b.dataset.kind !== 'all' && li.dataset.kind !== b.dataset.kind; });
    });
  });

  // "Poe arvustused": Loox's widget opens on product reviews; its own
  // currentTab=store parameter makes it open on the store reviews instead.
  document.querySelectorAll('.arc-store-rev').forEach((box) => {
    const toStore = () => {
      const frame = box.querySelector('iframe[src*="loox.io/widget"]');
      if (!frame || frame.src.includes('currentTab=store')) return !!frame;
      const url = new URL(frame.src);
      url.searchParams.set('currentTab', 'store');
      url.searchParams.set('default_tab', 'store');
      frame.src = url.toString();
      return true;
    };
    if (toStore()) return;
    const obs = new MutationObserver(() => { if (toStore()) obs.disconnect(); });
    obs.observe(box, { childList: true, subtree: true });
  });

  // Quick add: stays on the page, shows a short "added" state and opens the theme's cart notice.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-arc-add]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const status = form.querySelector('.arc-add__status');
    const errorBox = form.querySelector('.arc-add__error');
    if (button.dataset.state === 'loading') return;

    button.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');
    if (errorBox) errorBox.hidden = true;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = new FormData(form);
    if (cart && cart.getSectionsToRender) {
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    try {
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);

      button.dataset.state = 'added';
      if (status) status.textContent = form.dataset.addedText || '';
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'arc-quick-add' });
      }
      if (cart && cart.renderContents) {
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      }
      setTimeout(() => { button.dataset.state = ''; }, 1800);
    } catch (err) {
      button.dataset.state = '';
      if (errorBox) {
        errorBox.textContent = err.message || form.dataset.errorText || '';
        errorBox.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
})();
